<template>
  <div v-if="visible">
    <a href="/verify-email" class="cursor-pointer" data-testid="email-verification-banner" @click.prevent="handleBannerClick">
      <AlertBanner
        :status="helperStatus.Trust"
        class="banner-below-nav !leading-10"
        :full-page-banner="true"
        :auto-scroll-to-banner="false"
      >
        <span class="inline-block px-16 py-8">
          Let’s verify your email! It’s fast, keeps your account secure, and lets others know you’re real.
          <span class="underline font-medium">Verify email</span>
        </span>
      </AlertBanner>
    </a>
    <!-- spacer: banner is position:fixed below the nav -->
    <div class="h-52 md-1:h-56 w-full lg:hidden" aria-hidden="true">&nbsp;</div>
    <div class="hidden lg:block h-40 w-full" aria-hidden="true">&nbsp;</div>
  </div>
</template>

<script lang="ts">
import AlertBanner from '@/components/alert_banner/AlertBanner.vue';
import helperStatus from '@/helpers/status';
import shouldShowEmailVerificationBanner from '@/helpers/emailVerificationBannerLogic';
import { isSafeInternalPath, isVerifyEmailPath } from '@/apps/verifyEmail/helpers/paths';
import { EMAIL_VERIFICATION_STATUS } from '@/graphql/account/queries';
import { SEND_VERIFICATION_EMAIL } from '@/graphql/account/mutations';

export default {
  name: 'EmailVerificationBanner',
  components: {
    AlertBanner,
  },
  emits: ['visibility-change'],
  data() {
    return {
      helperStatus,
      showBannerFromApi: false,
      loaded: false,
    };
  },
  computed: {
    visible(): boolean {
      if (!this.loaded) {
        return false;
      }
      return shouldShowEmailVerificationBanner(this.showBannerFromApi, this.$route.path);
    },
    returnPath(): string {
      const fullPath = this.$route.fullPath;
      if (isSafeInternalPath(fullPath) && !isVerifyEmailPath(fullPath)) {
        return fullPath;
      }
      return '/';
    },
  },
  watch: {
    '$route.fullPath'() {
      void this.refreshStatus();
    },
    visible: {
      immediate: true,
      handler(isVisible: boolean) {
        this.$emit('visibility-change', isVisible);
      },
    },
  },
  mounted() {
    void this.refreshStatus();
    window.addEventListener('focus', this.onWindowFocus);
  },
  beforeUnmount() {
    window.removeEventListener('focus', this.onWindowFocus);
    this.$emit('visibility-change', false);
  },
  methods: {
    onWindowFocus(): void {
      void this.refreshStatus();
    },
    async refreshStatus(): Promise<void> {
      try {
        const { data } = await this.$apollo.query({
          query: EMAIL_VERIFICATION_STATUS,
          fetchPolicy: 'network-only',
        });
        this.showBannerFromApi = !!data?.emailVerificationStatus?.showBanner;
      } catch (error) {
        console.error('Error checking email verification banner eligibility:', error);
        this.showBannerFromApi = false;
      } finally {
        this.loaded = true;
      }
    },
    async handleBannerClick(): Promise<void> {
      // Kick off a verification email, then enter the existing waiting-page flow.
      // Failures are non-blocking — the waiting page still offers resend.
      try {
        await this.$apollo.mutate({
          mutation: SEND_VERIFICATION_EMAIL,
        });
      } catch (error) {
        console.error('Error sending verification email from banner:', error);
      }

      await this.$router.push({
        path: '/verify-email',
        query: { next: this.returnPath },
      });
    },
  },
};
</script>
